import React, { useEffect, useState, } from 'react';
import { ToggleButton, ToggleButtonGroup, Grid, Box, Typography, Link, useMediaQuery, TextField, InputAdornment } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { styled } from "@mui/material/styles";
import { CourseCard, CoursePagination, NoCourses } from '../../components/course';
import { course as courseConst } from '../course/constants';
import { useCourse } from './hooks';
import { useTheme } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GridViewIcon from '@mui/icons-material/GridView';
import TableRowsIcon from '@mui/icons-material/TableRows';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayLessonIcon from '@mui/icons-material/PlayLesson';
import SearchIcon from '@mui/icons-material/Search';
import Loader from '../../components/Loader';

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
    padding: theme.spacing(1),
    height: '30px',
    border: 'none',
    '&.Mui-selected': {
        background: 'transparent',
        '& .MuiSvgIcon-root': {
            color: 'var(--mui-palette-primary-orange)',
        },
    },
}));


export const Courses = () => {
    const theme = useTheme();
    const [view, setView] = useState('list');
    const navigate = useNavigate();
    const { courses, loadCourses, loading, remove } = useCourse();
    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState('');
    const pageSize = 8;

    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        if (isSmallScreen) {
            setView('card');
        } else {
            setView('list')
        }
    }, [isSmallScreen]);

    const onDelete = (courseId) => {
        if (!confirm('Are you sure to delete this course?')) { return; }

        remove({ id: courseId });
    }

    const handleViewChange = (event, newView) => {
        if (newView !== null) {
            setView(newView);
        }
    };

    // Debounced, server-side search: fetch the filtered list from the DB via the
    // `search` query param rather than filtering the loaded rows client-side. Empty
    // search loads immediately; typing is debounced (400ms) to avoid a request per keystroke.
    useEffect(() => {
        const query = search.trim();
        const payload = { category: "Course" };
        if (query) payload.search = query;
        // Grid (card) view shows a fixed 8 per page; list view keeps the default.
        if (view === 'card') payload.pageSize = pageSize;
        const delay = query ? 400 : 0;
        const timer = setTimeout(() => loadCourses(payload), delay);
        return () => clearTimeout(timer);
    }, [search, view]);

    const columns = [
        { field: 'courseName', headerName: 'Name', flex: 1 },
        { field: 'code', headerName: 'Code', flex: 1 },
        { field: 'location', headerName: 'Location', flex: 1, },
        {
            field: 'studentType',
            headerName: 'Geographical Type',
            flex: 1,
            renderCell: (params) => (
                <div>{courseConst.STUDENT_TYPE[params.row.studentType] || '—'}</div>
            ),
        },
        {
            field: 'courseMode', headerName: 'Mode', flex: 1, renderCell: (params) => {
                return <div>{courseConst.MODES[params.row.courseMode] || 'Unknown Mode'}</div>;
            }
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                const course = params.row;
                const editLink = `/courses/edit/${course.id}`;
                const lessonIcon = `/courses/${course.id}/lesson-plan`
                return (
                    <Box>
                           <Link style={styles.actionBtnItem} component={RouterLink} to={editLink} title="Edit Course"><CreateIcon sx={{ color: 'var(--mui-palette-primary-button)', fontSize: '1.2rem' }} /></Link>
                           <Link style={styles.actionBtnItem} component={RouterLink} to={lessonIcon} title="Lesson Plan"><PlayLessonIcon sx={{ color: 'var(--mui-palette-success-dark)', fontSize: '1.2rem', marginLeft: '5px' }} /></Link>
                        <Link style={styles.actionBtnItem} onClick={() => onDelete(course?.id)} title="Delete Course">
                            <DeleteIcon sx={{ color: 'var(--mui-palette-error-main)', fontSize: '1.2rem' }} />
                        </Link>
                    </Box>
                )
            }
        }
    ];

    return (
        <div>
            {loading ? <Loader /> : <>

                <div className='course'>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}>
                        <Typography
                            align="left"
                            variant="h1"
                            color="primary.blue"
                            sx={{
                                margin: '0',
                                fontWeight: '700',
                                lineHeight: '1.5',
                                fontSize: '1.8rem',
                            }}>Explore Courses</Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                [theme.breakpoints.down('sm')]: {
                                    flexDirection: 'row',
                                },
                            }}>
                            <ToggleButtonGroup style={styles.toggleButtonContainer}
                                sx={{
                                    [theme.breakpoints.down('sm')]: {
                                        justifyContent: 'flex-start',
                                    },
                                }}
                                value={view} exclusive onChange={handleViewChange} aria-label="course view">
                                <StyledToggleButton value="list" aria-label="list view" title="List View"
                                    sx={{
                                        padding: '5px',
                                        [theme.breakpoints.down('sm')]: {
                                            fontSize: '0.7rem'
                                        },
                                    }}><TableRowsIcon /></StyledToggleButton>
                                <StyledToggleButton value="card" aria-label="card view" title="Card View"
                                    sx={{
                                        padding: '5px',
                                        [theme.breakpoints.down('sm')]: {
                                            fontSize: '0.7rem'
                                        },
                                    }}><GridViewIcon /></StyledToggleButton>
                            </ToggleButtonGroup>
                            <div style={styles.create}>
                                <Link
                                    component="button"
                                    variant="body2"
                                    onClick={() => navigate('/courses/create')}
                                    title='Create Course'
                                    sx={{
                                        background: 'transparent',
                                        color: 'var(--mui-palette-primary-button)',
                                        p: 0,
                                    }}
                                >
                                    <AddCircleIcon sx={{ fontSize: '1.8rem', marginTop: '4px' }} />
                                </Link>
                            </div>
                        </Box>
                    </Box>
                </div>

                <Box sx={{ mt: 2 }}>
                    <TextField
                        size="small"
                        placeholder="Search by name or code"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{
                            width: { xs: '100%', sm: 320 },
                            backgroundColor: 'var(--mui-palette-common-white)',
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'var(--mui-palette-primary-blue)', fontSize: '1.2rem' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                <Box sx={{
                    clear: 'both',
                    width: '100%',
                    float: 'left',
                    mt: 2,
                    position: 'relative',
                    boxSizing: 'border-box',
                }}>
                    {view === 'card' ? (
                        <Grid container sx={{ position: 'relative', zIndex: 1, marginLeft: '-8px', marginRight: '-8px', width: 'auto' }}>
                            {courses?.length > 0 ? courses?.map(course => (
                                <Grid item key={course.id} xs={12} sm={6} md={4} lg={3} sx={{ p: 1 }}>
                                    <CourseCard course={course} />
                                </Grid>
                            )) : <NoCourses />}
                        </Grid>
                    ) : (
                        <div style={{ minHeight: 400, width: '100%', overflow: 'auto' }}>
                            {courses?.length > 0 ?
                                <DataGrid
                                    rows={courses}
                                    columns={columns}
                                    sx={{
                                        backgroundColor: 'var(--mui-palette-common-white)',
                                        [theme.breakpoints.down('sm')]: {
                                            width: '900px',
                                        },
                                        '& .css-yrdy0g-MuiDataGrid-columnHeaderRow': {
                                            background: 'var(--mui-palette-primary-blue) !important',
                                            color: 'var(--mui-palette-action-active)',
                                        },
                                        '& .MuiDataGrid-columnHeader': {
                                            background: 'var(--mui-palette-primary-blue) !important',
                                            color: 'var(--mui-palette-common-white)',
                                            '&:focus': {
                                                outline: 'none',
                                            }
                                        },
                                        '& .MuiDataGrid-columnHeaderTitle': {
                                            fontSize: '14px',
                                            fontWeight: 'bold',
                                        },
                                        // '& .MuiSvgIcon-root': {
                                        //     color: 'var(--mui-palette-common-white)',
                                        // },
                                    }}
                                    pageSize={5} />
                                : <NoCourses />}
                        </div>
                    )}
                    {courses?.length > 0 && <CoursePagination
                    />}
                </Box>
            </>}
        </div>
    );
};

const styles = {
    toggleButtonContainer: {
        display: 'flex',
        justifyContent: 'center'
    },
    create: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '0',
        marginBottom: '0',
        marginLeft: '5px',
    },
    courses: {
        marginTop: '2rem'
    },
    actionBtnItem: {
        marginLeft: '5px',
        marginRight: '5px',
        cursor: 'pointer'
    }
};